import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from './api.ts';

export interface ProductoCatalogo {
  id: string;
  referencia: string;
  nombre: string;
  marcaId: string;
  marcaNombre: string;
  categoria: string;
  tipo: string | null;
  coleccion: string | null;
  imagenUrl: string;
  fechaCompra: string;
  cantidadComprada: number;
  costo?: number;
  pvpSugerido: number | null;
  precioGuardado: {
    metodo: 'MANUAL' | 'PORCENTAJE' | 'MULTIPLICADOR' | 'SUMA';
    valorMetodo: number | null;
    precioAhora: number;
    precioAntes: number | null;
    estilo: 'SIMPLE' | 'ANTES_AHORA';
  } | null;
}

export interface FiltrosCatalogo {
  marca?: string;
  categoria?: string;
  tipo?: string;
  coleccion?: string;
  q?: string;
}

function querystring(filtros: FiltrosCatalogo, cursor?: string): string {
  const partes = new URLSearchParams({ limit: '60' });
  for (const [clave, valor] of Object.entries(filtros)) if (valor) partes.set(clave, valor);
  if (cursor) partes.set('cursor', cursor);
  return partes.toString();
}

export function useProductosCatalogo(filtros: FiltrosCatalogo, habilitado = true) {
  return useInfiniteQuery({
    enabled: habilitado,
    queryKey: ['catalogo-productos', filtros],
    queryFn: ({ pageParam }) =>
      api<{ items: ProductoCatalogo[]; siguienteCursor: string | null }>(
        `/catalogo/productos?${querystring(filtros, pageParam)}`,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.siguienteCursor ?? undefined,
  });
}

export function useFacetas() {
  return useQuery({
    queryKey: ['catalogo-facetas'],
    queryFn: () =>
      api<{ categorias: string[]; tipos: string[]; colecciones: string[] }>('/catalogo/facetas'),
    staleTime: 5 * 60 * 1000,
  });
}

/** Selección que viaja del panel al editor. Sesión del navegador y nada más (D25). */
const CLAVE_SELECCION = 'pm_seleccion';

export interface Seleccion {
  productoIds: string[];
  tipoContenido: string;
  /** Flujo nuevo: la plantilla se elige ANTES que los productos. */
  plantillaId?: string;
  plantillaNumProductos?: number;
  /** Material de galería a personalizar (D34): se vuelve `spec.materialId`. */
  materialId?: string;
  /** URL firmada del material, sólo para el preview local del editor. */
  materialUrl?: string;
}

export function guardarSeleccion(seleccion: Seleccion): void {
  sessionStorage.setItem(CLAVE_SELECCION, JSON.stringify(seleccion));
}

export function leerSeleccion(): Seleccion | null {
  const crudo = sessionStorage.getItem(CLAVE_SELECCION);
  if (!crudo) return null;
  try {
    return JSON.parse(crudo) as Seleccion;
  } catch {
    return null;
  }
}

/** Nombre comercial del formato ("WhatsApp 1:1") — el cliente no piensa en ratios. */
export function nombreFormato(f: string, t: (k: string) => string): string {
  const ratio = f.replace('R', '').replace('_', ':');
  const nombre = t(`formatos.${f}`);
  return nombre.startsWith('formatos.') ? ratio : `${nombre} ${ratio}`;
}
